import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import Layout from "../../components/Layout";
import { get, post } from "../../services/ApiService";
import { Icon, PageHeader, stripHtml, typeLabel } from "./icons";
import "./studio.css";

const RULE_LABEL = {
  DEFAULT: "Default",
  WITHIN_DAY_LIMIT: "Within day limit",
};

const TemplateEditor = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [sequence, setSequence] = useState([]);
  const [products, setProducts] = useState([]);
  const [library, setLibrary] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let stopped = false;

    const load = async () => {
      setLoading(true);
      try {
        const questionsRes = await get("api/questions/list");
        const questions = questionsRes.success && Array.isArray(questionsRes.questions)
          ? questionsRes.questions
          : [];

        let template = null;
        if (isEdit) {
          const templateRes = await get(`api/assessment-templates/${id}`);
          if (!templateRes.success) {
            if (!stopped) setError(templateRes.error || "Template not found");
            return;
          }
          template = templateRes.template;
        }

        if (stopped) return;
        setLibrary(questions);
        if (template) {
          setName(template.name || "");
          setIsActive(template.isActive !== false);
          setSequence(
            (template.questions || []).map((question) => ({
              _id: question._id,
              question: question.question,
              type: question.type,
            }))
          );
          setProducts(Array.isArray(template.products) ? template.products : []);
        }
        setError("");
      } catch {
        if (!stopped) setError("Could not load the template");
      } finally {
        if (!stopped) setLoading(false);
      }
    };

    load();
    return () => {
      stopped = true;
    };
  }, [id, isEdit]);

  const chosen = useMemo(() => new Set(sequence.map((question) => String(question._id))), [sequence]);

  const available = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return library.filter((question) => {
      if (chosen.has(String(question._id))) return false;
      if (!needle) return true;
      return stripHtml(question.question).toLowerCase().includes(needle);
    });
  }, [library, chosen, query]);

  const addQuestion = (question) => {
    setSequence((current) => [
      ...current,
      { _id: question._id, question: question.question, type: question.type },
    ]);
  };

  const move = (index, direction) => {
    setSequence((current) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= current.length) return current;
      const copy = [...current];
      const [item] = copy.splice(index, 1);
      copy.splice(nextIndex, 0, item);
      return copy;
    });
  };

  const remove = (questionId) => {
    setSequence((current) => current.filter((question) => question._id !== questionId));
  };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Name is required");
      return;
    }
    if (sequence.length === 0) {
      toast.error("Add at least one question");
      return;
    }

    try {
      setSaving(true);
      const body = {
        name: trimmed,
        isActive,
        questionIds: sequence.map((question) => question._id),
      };
      const res = isEdit
        ? await post(`api/assessment-templates/update/${id}`, body)
        : await post("api/assessment-templates/add", body);

      if (res.success) {
        toast.success(res.message);
        navigate("/assessment-templates");
      } else {
        toast.error(res.error || "Could not save template");
      }
    } catch {
      toast.error("Could not save template");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="at-studio">
        <PageHeader
          kicker="Assessments"
          title={isEdit ? "Edit template" : "New template"}
          subtitle={
            products.length === 1
              ? "1 product is assigned to this template."
              : `${products.length} products are assigned to this template.`
          }
        >
          <Link className="at-btn" to="/assessment-templates">
            <Icon name="back" />
            Cancel
          </Link>
          <button type="button" className="at-btn at-btn-primary" onClick={save} disabled={saving || loading || Boolean(error)}>
            {saving ? "Saving…" : "Save template"}
          </button>
        </PageHeader>

        {loading && <div className="at-panel"><div className="at-empty">Loading…</div></div>}
        {!loading && error && <div className="at-panel"><div className="at-empty">{error}</div></div>}

        {!loading && !error && (
          <>
            <div className="at-namebar">
              <div className="at-field">
                <label htmlFor="template-name">Name</label>
                <input
                  id="template-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Sildenafil — within day limit"
                />
              </div>
              <button
                type="button"
                className={`at-btn at-toggle ${isActive ? "at-btn-primary" : ""}`}
                onClick={() => setIsActive((current) => !current)}
              >
                {isActive ? "Active" : "Inactive"}
              </button>
            </div>

            <div className="at-board">
              <section className="at-panel at-questions" aria-label="Questions">
                <div className="at-panel-head">
                  <h2>
                    Questions
                    <span className="at-count"> · {available.length}</span>
                  </h2>
                  <label className="at-search">
                    <Icon name="search" />
                    <input
                      type="search"
                      value={query}
                      placeholder="Search questions"
                      onChange={(event) => setQuery(event.target.value)}
                    />
                  </label>
                </div>
                <div className="at-scroll">
                  {available.length === 0 && (
                    <div className="at-empty">
                      {library.length === 0 ? "No questions yet." : "No questions left to add."}
                    </div>
                  )}
                  {available.map((question) => (
                    <button
                      type="button"
                      className="at-pick"
                      key={question._id}
                      onClick={() => addQuestion(question)}
                    >
                      <span className="at-pick-text">
                        <strong>{stripHtml(question.question)}</strong>
                        <span className="at-muted">{typeLabel(question.type)}</span>
                      </span>
                      <span className="at-icon-btn" aria-hidden="true">
                        <Icon name="plus" size={14} />
                      </span>
                    </button>
                  ))}
                </div>
              </section>

              <section className="at-panel at-sequence" aria-label="Sequence">
                <div className="at-panel-head">
                  <h2>
                    Sequence
                    <span className="at-count">
                      {" "}· {sequence.length} {sequence.length === 1 ? "question" : "questions"}
                    </span>
                  </h2>
                </div>
                <div className="at-scroll">
                  {sequence.length === 0 && (
                    <div className="at-empty">
                      Add questions from the left. This order is the order used in the assessment.
                    </div>
                  )}
                  {sequence.map((question, index) => (
                    <div className="at-seq" key={question._id}>
                      <span className="at-num">{index + 1}</span>
                      <span className="at-seq-text">
                        <strong>{stripHtml(question.question)}</strong>
                        <span className="at-muted">{typeLabel(question.type)}</span>
                      </span>
                      <span className="at-seq-actions">
                        <button
                          type="button"
                          className="at-icon-btn"
                          aria-label="Move question up"
                          disabled={index === 0}
                          onClick={() => move(index, -1)}
                        >
                          <Icon name="up" size={15} />
                        </button>
                        <button
                          type="button"
                          className="at-icon-btn"
                          aria-label="Move question down"
                          disabled={index === sequence.length - 1}
                          onClick={() => move(index, 1)}
                        >
                          <Icon name="down" size={15} />
                        </button>
                        <button
                          type="button"
                          className="at-icon-btn is-danger"
                          aria-label="Remove question"
                          onClick={() => remove(question._id)}
                        >
                          <Icon name="trash" size={15} />
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {isEdit && (
              <section className="at-assigned" aria-label="Products using this template">
                <div className="at-stat">
                  <span>Assigned products</span>
                  <strong>{products.length}</strong>
                  <small>{products.length === 1 ? "Uses this template" : "Use this template"}</small>
                </div>
                <div className="at-product-list">
                  {products.length === 0 && (
                    <p className="at-product-empty">No products use this template yet.</p>
                  )}
                  {products.map((product) => (
                    <Link
                      key={product._id}
                      className="at-product-row"
                      to={`/products/${product._id}/assessment-rules`}
                    >
                      <span className="at-product-copy">
                        <strong>{product.title}</strong>
                        {product.sku ? <span>{product.sku}</span> : null}
                      </span>
                      <span className="at-product-rules">
                        {(product.conditions || []).map((condition) => (
                          <span className="at-pill is-on" key={condition}>
                            {RULE_LABEL[condition] || condition}
                          </span>
                        ))}
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default TemplateEditor;
